import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { customerAPI, vehicleAPI } from '../api';
import { Button } from '../components/Button';
import AddVehicleModal from '../components/modals/AddVehicleModal';
import { PageHeader } from '../components/PageHeader';
import { useAnalytics } from '../hooks/useAnalytics';
import type { Vehicle } from '../types';

export function VehiclesPage() {
  const navigate = useNavigate()
  const { trackButtonClick } = useAnalytics()
  const [filters, setFilters] = useState({
    vehicle_type: '',
    fuel_type: '',
  })
  const [showCustomerPicker, setShowCustomerPicker] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedCustomerId, setSelectedCustomerId] = useState('')
  const [customerSearch, setCustomerSearch] = useState('')

  const { data: customerData, isLoading: customersLoading } = useQuery({
    queryKey: ['customers', 'vehicle-picker'],
    queryFn: () =>
      customerAPI.list({ limit: 200, offset: 0, active: true }).then((r) => r.data),
    enabled: showCustomerPicker,
  })
  const customers = customerData?.customers || []

  const filteredCustomers = customers.filter((customer) =>
    `${customer.full_name} ${customer.phone}`.toLowerCase().includes(customerSearch.toLowerCase())
  )

  const formatVehicleType = (type?: string | null) => {
    if (!type) return 'Not set'

    const labels: Record<string, string> = {
      two_wheeler: '2 Wheeler',
      three_wheeler: '3 Wheeler',
      four_wheeler: '4 Wheeler',
      commercial: 'Commercial',
      other: 'Other',
    }

    return labels[type] || type.replace(/_/g, ' ')
  }

  const formatFuelType = (fuel?: string | null) => {
    if (!fuel) return 'Not set'

    const labels: Record<string, string> = {
      petrol: 'Petrol',
      diesel: 'Diesel',
      electric: 'Electric',
      cng: 'CNG',
      hybrid: 'Hybrid',
      lpg: 'LPG',
    }

    return labels[fuel] || fuel.toUpperCase()
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['vehicles', filters],
    queryFn: () =>
      vehicleAPI
        .listAll({
          vehicle_type: filters.vehicle_type || undefined,
          fuel_type: filters.fuel_type || undefined,
        })
        .then((r) => r.data),
  })

  const vehicles = data?.vehicles || []
  const total = data?.total || 0

  return (
    <div className="space-y-6">
      <PageHeader
        title="Vehicles"
        subtitle={`Total: ${total} vehicles`}
        action={{
          label: "+ Add Vehicle",
          onClick: () => {
            trackButtonClick('Add Vehicle', { page: 'vehicles' })
            setShowCustomerPicker(true)
          },
        }}
      />

      {/* Customer Picker */}
      {showCustomerPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Customer</h3>
            <input
              type="text"
              placeholder="Search by name or phone..."
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-4 py-2 mb-3 focus:border-saffron focus:outline-none"
              disabled={customersLoading}
              autoFocus
            />
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
              {filteredCustomers.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  {customerSearch ? 'No customers found' : 'No customers available'}
                </div>
              ) : (
                filteredCustomers.map((customer) => (
                  <button
                    key={customer.id}
                    onClick={() => {
                      trackButtonClick('Select Customer for Vehicle', { customerId: customer.id, customerName: customer.full_name })
                      setSelectedCustomerId(customer.id)
                      setShowCustomerPicker(false)
                      setShowAddModal(true)
                    }}
                    className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-saffron/5 transition-colors ${
                      selectedCustomerId === customer.id ? 'bg-saffron/10' : ''
                    }`}
                  >
                    <div className="font-medium text-gray-900">{customer.full_name}</div>
                    <div className="text-sm text-gray-500">{customer.phone}</div>
                  </button>
                ))
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button 
                variant="secondary" 
                onClick={() => {
                  trackButtonClick('Cancel Customer Selection', { page: 'vehicles' })
                  setShowCustomerPicker(false)
                  setSelectedCustomerId('')
                  setCustomerSearch('')
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Vehicle Modal */}
      {showAddModal && (
        <AddVehicleModal
          open={showAddModal}
          onClose={() => {
            setShowAddModal(false)
            setSelectedCustomerId('')
            setCustomerSearch('')
          }}
          customerId={selectedCustomerId}
          onSuccess={() => {
            setShowAddModal(false)
            setSelectedCustomerId('')
          }}
        />
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Vehicle Type
            </label>
            <select
              value={filters.vehicle_type}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  vehicle_type: e.target.value,
                }))
              }
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">All Types</option>
              <option value="two_wheeler">2 Wheeler</option>
              <option value="three_wheeler">3 Wheeler</option>
              <option value="four_wheeler">4 Wheeler</option>
              <option value="commercial">Commercial</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Fuel Type</label>
            <select
              value={filters.fuel_type}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, fuel_type: e.target.value }))
              }
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">All Fuels</option>
              <option value="petrol">Petrol</option>
              <option value="diesel">Diesel</option>
              <option value="electric">Electric</option>
              <option value="cng">CNG</option>
              <option value="hybrid">Hybrid</option>
              <option value="lpg">LPG</option>
            </select>
          </div>
        </div>
      </div>

      {/* Vehicles List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">
            Loading vehicles...
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-500">
            Error loading vehicles
          </div>
        ) : vehicles.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No vehicles found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold">
                    Registration
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">
                    Make / Model
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">
                    Fuel Type
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {vehicles.map((vehicle: Vehicle) => (
                  <tr key={vehicle.id} className="border-b hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium">
                      {vehicle.registration_number}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {formatVehicleType(vehicle.vehicle_type)}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {[vehicle.make, vehicle.model]
                        .filter(Boolean)
                        .join(" / ") || "Not provided"}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {formatFuelType(vehicle.fuel_type)}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="font-medium text-gray-900">
                        {vehicle.owner_name || "Unknown customer"}
                      </div>
                      <div className="text-xs text-gray-500">
                        {vehicle.owner_phone || "-"}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <button
                        onClick={() => {
                          trackButtonClick('View Vehicle Details', { vehicleId: vehicle.id, customerId: vehicle.customer_id })
                          navigate(
                            `/customers/${vehicle.customer_id}/vehicles/${vehicle.id}`,
                          )
                        }}
                        className="text-saffron hover:underline"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
